import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Box } from '@mui/material';
import MessageBox from 'src/components/MessageBox';
import { ROUTES } from 'src/constants';
import {
  setCurrentNavigationTab,
  setShouldScrollToPortPin,
  dismissTelnyxMigrationBanner,
  openTelnyxMigrationPinModal,
} from 'src/actions/accountSettingsActions';
import {
  showPBTextingModal,
  dismissPBTextingBannerForSession,
  showWhatsNew,
  dismissWhatsNewBannerForSession,
} from 'src/actions/accountActions';
import { checkActiveImport, getImportBatchStatus } from 'src/actions/customersActions';
import helpers from 'src/helpers';
import { shouldShowPortInProgressBanner } from 'src/helpers/telnyxPortPendingGates';
import {
  readMigrationBannerDismissed,
  writeMigrationBannerDismissed,
  bannerHeadlineSentence,
} from 'src/helpers/telnyxMigrationPin';
import { isSafeAppPath } from 'src/helpers/postLoginRedirect';

// Session dismissal for the outside-send-hours banner.
//
// Keyed by date because the banner's content IS today's send window, so a
// dismissal must not carry into tomorrow's. sessionStorage rather than
// localStorage so it also clears when the tab is closed -- note that it
// SURVIVES a sign-out and sign-in in the same tab, since nothing clears
// sessionStorage on logout. The point is that it cannot be hidden for good:
// this is an informational banner, not a setting.
//
// Only this banner gets a dismiss. The blocking states -- subscription
// expired, texting invoice unpaid, PB API invalid, automated messages paused,
// port in progress, trial expiring -- stay non-dismissible on purpose, so a
// consultant cannot hide the fact that their texts will not send.
const OUTSIDE_SEND_HOURS_DISMISS_KEY_PREFIX = 'cp.dismissed.outsideSendHours';

const getOutsideSendHoursDismissKey = (timeZone) => {
  const today = moment().tz(timeZone || 'America/New_York').format('YYYY-MM-DD');
  return `${OUTSIDE_SEND_HOURS_DISMISS_KEY_PREFIX}:${today}`;
};

const readOutsideSendHoursDismissed = (timeZone) => {
  try {
    return sessionStorage.getItem(getOutsideSendHoursDismissKey(timeZone)) === '1';
  } catch (e) {
    // sessionStorage unavailable (private mode, blocked cookies) -- the
    // banner simply stays visible, which is the safe direction.
    return false;
  }
};

function DashboardLayoutTopMessage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [importCompleteDismissed, setImportCompleteDismissed] = useState(false);
  const [revertCompleteDismissed, setRevertCompleteDismissed] = useState(false);
  const [outsideSendHoursDismissed, setOutsideSendHoursDismissed] = useState(false);
  // Banner state the consultant closed in this render tree (see below).
  const [dismissedMigrationBannerState, setDismissedMigrationBannerState] = useState(null);

  const {
    user,
    pbAPIKeyValid,
    userMessageSendTimes,
    pbTextingModal,
    whatsNew,
    telnyxMigrationBanner,
    telnyxMigrationPin,
  } = useSelector((state) => state.account);
  const { activeImport, activeRevert } = useSelector((state) => state.customers || {});
  const {
    isTrialVersionWithoutSubscription,
    subscriptionOff,
    userSubscription,
    timeZone,
    textingProvider,
  } = user || {};
  const { isSubscriptionActive } = userSubscription || {};

  // Update current time every minute to recalculate send window status
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // CON-1459: Bootstrap activeImport from server on mount
  useEffect(() => {
    dispatch(checkActiveImport());
  }, [dispatch]);

  // CON-1459: Poll for active import status
  const importPollingRef = useRef(null);
  useEffect(() => {
    const isActive = activeImport?.importBatchId
      && (activeImport.status === 'queued' || activeImport.status === 'processing');

    if (isActive) {
      setImportCompleteDismissed(false);
      importPollingRef.current = setInterval(() => {
        dispatch(getImportBatchStatus(activeImport.importBatchId));
      }, 10000);
    }

    return () => {
      if (importPollingRef.current) {
        clearInterval(importPollingRef.current);
        // Null the ref so a later cleanup cannot clear an id that has
        // already been cleared and reassigned by a newer effect run.
        importPollingRef.current = null;
      }
    };
  }, [dispatch, activeImport?.importBatchId, activeImport?.status]);

  // CON-1460: Poll for active revert status
  const revertPollingRef = useRef(null);
  useEffect(() => {
    const isActive = activeRevert?.importBatchId
      && (activeRevert.revertStatus === 'queued' || activeRevert.revertStatus === 'processing');

    if (isActive) {
      setRevertCompleteDismissed(false);
      setImportCompleteDismissed(true); // dismiss import bar so revert bar shows
      revertPollingRef.current = setInterval(() => {
        dispatch(getImportBatchStatus(activeRevert.importBatchId));
      }, 10000);
    }

    return () => {
      if (revertPollingRef.current) {
        clearInterval(revertPollingRef.current);
        revertPollingRef.current = null;
      }
    };
  }, [dispatch, activeRevert?.importBatchId, activeRevert?.revertStatus]);

  // Hydrate the dismissal from sessionStorage, and re-check it on the same
  // one-minute tick that recalculates the send window. That tick is what
  // carries the dismissal across midnight: after the date rolls over the key
  // no longer exists, so the banner comes back for the new day's hours.
  useEffect(() => {
    setOutsideSendHoursDismissed(readOutsideSendHoursDismissed(timeZone));
  }, [timeZone, currentTime]);

  // Texting-move banner session dismissal. Read synchronously (no effect)
  // so a closed banner does not flash back for one frame on reload. Keyed by
  // banner state, so a new stage of the move shows again after an earlier
  // close. The local state covers storage being unavailable.
  const migrationBannerState = telnyxMigrationBanner?.state ?? 'default';
  const migrationBannerDismissed = dismissedMigrationBannerState === migrationBannerState
    || readMigrationBannerDismissed(user?.id, migrationBannerState);

  const handleDismissMigrationBanner = (e) => {
    e.stopPropagation();
    writeMigrationBannerDismissed(user?.id, migrationBannerState);
    setDismissedMigrationBannerState(migrationBannerState);
    // Best effort; the banner is already hidden for this session.
    dispatch(dismissTelnyxMigrationBanner());
  };

  // "Enter code" opens the code modal where she is, rather than sending her
  // to /app/verify (which stays for the links in texts and emails).
  const handleOpenMigrationPinModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(openTelnyxMigrationPinModal());
  };

  const handleDismissOutsideSendHours = (e) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(getOutsideSendHoursDismissKey(timeZone), '1');
    } catch (err) {
      // sessionStorage unavailable -- dismiss for this render only.
    }
    setOutsideSendHoursDismissed(true);
  };

  const handleClickSubscribe = () => {
    dispatch(setCurrentNavigationTab('subscription'));
  };

  // ✅ Use useMemo to recalculate when userMessageSendTimes changes
  const isOutsideSendHours = useMemo(() => {
    if (!userMessageSendTimes) return false;
    return !helpers.checkMessageSendHours.isWithinMessageSendHours(
      userMessageSendTimes,
      timeZone || 'America/New_York',
      textingProvider || 'PB'
    );
  }, [userMessageSendTimes, timeZone, textingProvider, currentTime]);

  // ✅ Use useMemo to recalculate when userMessageSendTimes changes
  const sendTimeMessage = useMemo(() => {
    if (!userMessageSendTimes) return '';
    return helpers.checkMessageSendHours.getNextSendTimeMessage(
      userMessageSendTimes,
      timeZone || 'America/New_York'
    );
  }, [userMessageSendTimes, timeZone]);

  // CON-1459: Import/revert progress — HIGHEST PRIORITY
  // Active background operations must always be visible, even above
  // PB banners, subscription warnings, and page-blocked checks,
  // because they reset on hard refresh and the user needs to see them.
  if (activeImport?.importBatchId) {
    const { status: importStatus, totalRows, processedRows } = activeImport;

    if (importStatus === 'queued' || importStatus === 'processing') {
      const progress = totalRows
        ? `${processedRows || 0} / ${totalRows}`
        : '...';
      return (
        <MessageBox
          message={(
            <>
              <strong>Importing contacts...</strong>
              <span className="nbsp" />
              {progress}
              <span className="nbsp xs-full" />
              <Link
                className="a-inherit"
                to="/app/management/customers/import-history"
              >
                View Import History
              </Link>
            </>
          )}
          type="type-3-light"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }

    if (importStatus === 'failed') {
      return (
        <MessageBox
          message={(
            <>
              <strong>Import failed.</strong>
              <span className="nbsp xs-full" />
              <Link
                className="a-inherit"
                to="/app/management/customers/import-history"
              >
                View Import History
              </Link>
            </>
          )}
          type="type-3-red"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }

    if (importStatus === 'completed' && !importCompleteDismissed) {
      const count = processedRows || totalRows || 0;
      return (
        <MessageBox
          message={(
            <span style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            >
              <span style={{ flex: 1, textAlign: 'center' }}>
                <strong>
                  Import complete
                  {count > 0 && (
                    <>
                      {' — '}
                      {count}
                      {' contacts imported'}
                    </>
                  )}
                </strong>
                <span className="nbsp xs-full" />
                <Link
                  className="a-inherit"
                  to="/app/management/customers/import-history"
                >
                  View Import History
                </Link>
              </span>
              <button
                type="button"
                onClick={() => setImportCompleteDismissed(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginLeft: 8,
                  fontSize: 16,
                  color: 'inherit',
                  opacity: 0.7,
                }}
                aria-label="Dismiss notification"
              >
                ✕
              </button>
            </span>
          )}
          type="type-3-light"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }
  }

  // CON-1460: Revert progress notification
  if (activeRevert?.importBatchId) {
    const {
      revertStatus,
      totalRows: revertTotalRows,
      revertedRows,
    } = activeRevert;

    if (revertStatus === 'queued' || revertStatus === 'processing') {
      const progress = revertTotalRows
        ? `${revertedRows || 0} / ${revertTotalRows}`
        : '...';
      return (
        <MessageBox
          message={(
            <>
              <strong>Reverting import...</strong>
              <span className="nbsp" />
              {progress}
              <span className="nbsp xs-full" />
              <Link
                className="a-inherit"
                to="/app/management/customers/import-history"
              >
                View Import History
              </Link>
            </>
          )}
          type="type-3-light"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }

    if (revertStatus === 'failed') {
      return (
        <MessageBox
          message={(
            <>
              <strong>Revert failed.</strong>
              <span className="nbsp xs-full" />
              <Link
                className="a-inherit"
                to="/app/management/customers/import-history"
              >
                View Import History
              </Link>
            </>
          )}
          type="type-3-red"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }

    if (revertStatus === 'completed' && !revertCompleteDismissed) {
      const revertCount = revertedRows || revertTotalRows || 0;
      return (
        <MessageBox
          message={(
            <span style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            >
              <span style={{ flex: 1, textAlign: 'center' }}>
                <strong>
                  Revert complete
                  {revertCount > 0 && (
                    <>
                      {' — '}
                      {revertCount}
                      {' contacts reverted'}
                    </>
                  )}
                </strong>
                <span className="nbsp xs-full" />
                <Link
                  className="a-inherit"
                  to="/app/management/customers/import-history"
                >
                  View Import History
                </Link>
              </span>
              <button
                type="button"
                onClick={() => setRevertCompleteDismissed(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginLeft: 8,
                  fontSize: 16,
                  color: 'inherit',
                  opacity: 0.7,
                }}
                aria-label="Dismiss notification"
              >
                ✕
              </button>
            </span>
          )}
          type="type-3-light"
          paddingTop="0"
          paddingBottom="0"
          visible
          fullWidth
        />
      );
    }
  }

  // Texting-code step. Server-driven copy from the login and /me payloads
  // (account.telnyxMigrationBanner). Slotted AFTER Import/Revert progress and
  // BEFORE port-in-progress. Closeable only when the server marks it
  // dismissible (the server then hides it for 24 hours or until her state
  // changes). Not shown on /app/verify, which is the same form.
  const {
    headline: migrationHeadline,
    body: migrationBody,
    ctaLabel: migrationCtaLabel,
    ctaUrl: migrationCtaUrl,
    dismissible: migrationDismissible,
  } = telnyxMigrationBanner || {};
  const isOnVerifyPage = location?.pathname === ROUTES.APP_VERIFY;
  if (
    (migrationHeadline || migrationBody)
    && !(migrationDismissible && migrationBannerDismissed)
    && !isOnVerifyPage
  ) {
    // ctaUrl is a relative app path; anything else is not rendered.
    const showMigrationCta = !!migrationCtaLabel && isSafeAppPath(migrationCtaUrl);
    // With a code waiting, "Enter code" opens the modal; otherwise it links.
    const ctaOpensModal = telnyxMigrationPin?.required === true;

    return (
      <MessageBox
        message={(
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
          >
            <span style={{ flex: 1, textAlign: 'center' }}>
              {migrationHeadline && (
                <strong>{bannerHeadlineSentence(migrationHeadline)}</strong>
              )}
              {migrationHeadline && migrationBody && ' '}
              {migrationBody}
              {showMigrationCta && ' '}
              {showMigrationCta && ctaOpensModal && (
                <Link
                  className="a-inherit"
                  to={migrationCtaUrl}
                  onClick={handleOpenMigrationPinModal}
                >
                  {migrationCtaLabel}
                </Link>
              )}
              {showMigrationCta && !ctaOpensModal && (
                <Link
                  className="a-inherit"
                  to={migrationCtaUrl}
                >
                  {migrationCtaLabel}
                </Link>
              )}
            </span>
            {migrationDismissible && (
              <button
                type="button"
                onClick={handleDismissMigrationBanner}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginLeft: 8,
                  fontSize: 16,
                  color: 'inherit',
                  opacity: 0.7,
                }}
                aria-label="Dismiss banner"
              >
                ✕
              </button>
            )}
          </span>
        )}
        type="type-3-light-blue"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // CON-1488: Telnyx port-pending banner. Shows when the user has saved a
  // port draft but the port has not yet been submitted to Telnyx. The
  // banner is the persistent way back to the Port tab to enter the
  // port-out PIN once it arrives from their current carrier (1-2 day
  // wait). Non-dismissible by design — disappearing the moment the row
  // status transitions to 'submitted'. Slotted AFTER Import/Revert
  // (highest-priority operational banners) and BEFORE PB Texting / What's
  // New (marketing banners). Legacy Lumian users never have
  // hasSavedPortDraft set, and a queued Lumian v2 draft is excluded by the
  // predicate, so this block never fires for Lumian users.
  if (shouldShowPortInProgressBanner(user)) {
    const handlePortBannerClick = () => {
      dispatch(setCurrentNavigationTab('phoneNumberPort'));
      // Signal AccountDetailsStep that this navigation came from the banner
      // (vs. the user clicking the tab themselves) so it scrolls the
      // Port-out PIN field into view and focuses it on mount. The flag is
      // a one-shot — AccountDetailsStep clears it after consuming.
      dispatch(setShouldScrollToPortPin(true));
    };

    return (
      <MessageBox
        message={(
          <span
            role="button"
            tabIndex={0}
            onClick={handlePortBannerClick}
            onKeyPress={(e) => e.key === 'Enter' && handlePortBannerClick()}
            style={{
              cursor: 'pointer',
              display: 'block',
              textAlign: 'center',
              width: '100%',
            }}
          >
            <strong>Port in progress.</strong>
            {' '}
            <Link
              to={ROUTES.APP_ACCOUNT}
              className="a-inherit"
              onClick={handlePortBannerClick}
            >
              Click here
            </Link>
            {' '}
            to enter your port PIN when you receive it from your current carrier.
          </span>
        )}
        type="type-3-light-blue"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // PB Texting Sunset Banner
  const { shouldShowBanner, bannerDismissedThisSession } = pbTextingModal || {};
  if (shouldShowBanner && !bannerDismissedThisSession) {
    const handleBannerClick = () => {
      dispatch(showPBTextingModal());
    };

    const handleBannerDismiss = (e) => {
      e.stopPropagation();
      dispatch(dismissPBTextingBannerForSession());
    };

    const bannerOuterStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    };

    const bannerTextStyle = {
      cursor: 'pointer',
      flex: 1,
      textAlign: 'center',
    };

    const dismissButtonStyle = {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px 8px',
      marginLeft: 8,
      fontSize: 16,
      color: 'inherit',
      opacity: 0.7,
    };

    return (
      <MessageBox
        message={(
          <span style={bannerOuterStyle}>
            <span
              role="button"
              tabIndex={0}
              onClick={handleBannerClick}
              onKeyPress={(e) => e.key === 'Enter' && handleBannerClick()}
              style={bannerTextStyle}
            >
              CP now has texting built in! Click here to learn more.
            </span>
            <button
              type="button"
              onClick={handleBannerDismiss}
              style={dismissButtonStyle}
              aria-label="Dismiss banner"
            >
              ✕
            </button>
          </span>
        )}
        type="type-3-light"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // What's New Banner
  const {
    shouldShowBanner: whatsNewShouldShowBanner,
    bannerDismissedThisSession: whatsNewBannerDismissed,
    permanentlyDismissed: whatsNewPermanentlyDismissed,
  } = whatsNew || {};
  if (whatsNewShouldShowBanner && !whatsNewBannerDismissed && !whatsNewPermanentlyDismissed) {
    const handleWhatsNewBannerClick = () => {
      dispatch(showWhatsNew());
    };

    const handleWhatsNewBannerDismiss = (e) => {
      e.stopPropagation();
      dispatch(dismissWhatsNewBannerForSession());
    };

    return (
      <MessageBox
        message={(
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={handleWhatsNewBannerClick}
              onKeyPress={(e) => e.key === 'Enter' && handleWhatsNewBannerClick()}
              style={{
                cursor: 'pointer',
                flex: 1,
                textAlign: 'center',
              }}
            >
              🆕
              {' '}
              <strong>What&apos;s New in CP</strong>
              {' '}
              — click here to see what&apos;s new. Open to dismiss permanently.
            </span>
            <button
              type="button"
              onClick={handleWhatsNewBannerDismiss}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                marginLeft: 8,
                fontSize: 16,
                color: 'inherit',
                opacity: 0.7,
              }}
              aria-label="Dismiss banner"
            >
              ✕
            </button>
          </span>
        )}
        type="type-3-light"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  if (helpers.isPageBlocked(user) || subscriptionOff) {
    return null;
  }

  // Subscription expired
  if (!helpers.doesUserHaveSubscription(user)) {
    return (
      <MessageBox
        message={(
          <>
            Your Subscription has Expired!
            <span className="nbsp xs-full" />
            Please
            <span className="nbsp" />
            <Link
              className="a-inherit"
              to={ROUTES.APP_ACCOUNT}
              onClick={handleClickSubscribe}
            >
              Renew
            </Link>
            .
          </>
        )}
        type="type-3-red"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // CON-1481 — Metered invoice unpaid (texting paused). Sits AFTER the base
  // subscription-expired check so base takes priority.
  if (userSubscription?.meteredInvoiceUnpaid) {
    return (
      <MessageBox
        message={(
          <>
            Your texting invoice is past due — texts won&apos;t send until you pay it.
            <span className="nbsp xs-full" />
            <Link
              className="a-inherit"
              to={ROUTES.APP_ACCOUNT}
              onClick={handleClickSubscribe}
            >
              Pay now
            </Link>
            .
          </>
        )}
        type="type-3-red"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // PB API invalid
  if (!pbAPIKeyValid) {
    return (
      <MessageBox
        message={(
          <>
            Connection Point could NOT connect to your PB account! Please sign out and back in to CP to try again.
            <br />
            If you have cancelled your PB account or changed your PB # or API Key, please text CP Support - 317-456-5610.
            <br />
            Until this is resolved, you won't be able to access PB features, such as updating or processing orders, sending smart actions, etc.
          </>
        )}
        type="type-3-red"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // Automated messages paused
  if (userMessageSendTimes && !userMessageSendTimes.isActive && textingProvider === 'CP') {
    const isPausedByDailyLimit = userMessageSendTimes.isActiveSetBasedOnMaxMessages;
    const { permissions: bannerUserPerms } = user?.userRole || {};
    const canViewGeneralTab = helpers.permissions.doesUserHavePermission(
      'MESSAGING_SETTINGS_GENERAL_VIEW',
      bannerUserPerms
    ) && helpers.permissions.doesUserHavePermission(
      'MESSAGING_SETTINGS_DAILY_LIMIT_INCREASE',
      bannerUserPerms
    );

    return (
      <MessageBox
        message={(
          <>
            <strong>⏸️ Automated Messages are Paused</strong>
            <span className="nbsp xs-full" />
            {isPausedByDailyLimit
              ? 'You have exceeded your daily text limit.'
              : "Automated messages won't send until you turn them back on."}
            <span className="nbsp xs-full" />
            {isPausedByDailyLimit && canViewGeneralTab ? (
              <Link
                className="a-inherit"
                to="/app/settings/messaging-settings"
              >
                Increase your Daily Limit
              </Link>
            ) : (
              <Link
                className="a-inherit"
                to="/app/settings/messaging-settings"
              >
                Update Settings
              </Link>
            )}
          </>
        )}
        type="type-3-red"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // Priority 4: Outside send hours message (only show if automated messages are active)
  if (
    userMessageSendTimes?.isActive
    && isOutsideSendHours
    && sendTimeMessage
    && !outsideSendHoursDismissed
  ) {
    const isDayBlocked = sendTimeMessage.includes('blocked');
    const shortRange = helpers.checkMessageSendHours.getSendHoursRange(
      userMessageSendTimes,
      timeZone || 'America/New_York'
    );
    // A blocked day already reads short, so it keeps one wording. Only the
    // hours sentence gets a narrow-viewport variant.
    const hasShortWording = !isDayBlocked && !helpers.isEmpty(shortRange);

    return (
      <MessageBox
        message={(
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
          >
            <span style={{ flex: 1, textAlign: 'center' }}>
              {hasShortWording ? (
                <>
                  <Box
                    component="span"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    <strong>⏰ Outside Automated Message Send Hours. </strong>
                    {sendTimeMessage}
                  </Box>
                  {/* Narrow viewports get the same meaning in fewer
                      characters. "Automated" is kept deliberately: this
                      banner is about automated sends being held, while
                      manual texting still works, and dropping the word
                      would read as "you cannot text at all". */}
                  <Box
                    component="span"
                    sx={{ display: { xs: 'inline', sm: 'none' } }}
                  >
                    <strong>⏰ Outside Automated Send Hours: </strong>
                    {shortRange}
                  </Box>
                </>
              ) : (
                <>
                  <strong>
                    {isDayBlocked ? '🚫 ' : '⏰ Outside Automated Message Send Hours. '}
                  </strong>
                  {sendTimeMessage}
                </>
              )}
              {/* A plain nbsp rather than "nbsp xs-full". xs-full forces a
                  hard line break below sm, which put Update Settings on a
                  line of its own and made this banner three lines tall on a
                  phone. */}
              <span className="nbsp" />
              <Link
                className="a-inherit"
                to="/app/settings/messaging-settings"
              >
                Update Settings
              </Link>
            </span>
            <button
              type="button"
              onClick={handleDismissOutsideSendHours}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                marginLeft: 8,
                fontSize: 16,
                color: 'inherit',
                opacity: 0.7,
              }}
              aria-label="Dismiss banner"
            >
              ✕
            </button>
          </span>
        )}
        type="type-3"
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  // Priority 5: Trial expiring (if still used)
  if (isTrialVersionWithoutSubscription && !isSubscriptionActive) {
    return (
      <MessageBox
        message={(
          <>
            Your TRIAL period ends in
            <span className="nbsp" />
            {helpers.transform.toDayDifferent(isTrialVersionWithoutSubscription, moment())}
            <span className="nbsp" />
            day(s)!
            <span className="nbsp xs-full" />
            Please
            <span className="nbsp" />
            <Link
              className="a-inherit"
              to={ROUTES.APP_ACCOUNT}
              onClick={handleClickSubscribe}
            >
              Subscribe
            </Link>
            . You won't be billed until trial ends.
          </>
        )}
        type={helpers.isDateAfterCurrent(isTrialVersionWithoutSubscription, moment().utc().add(8, 'd')) ? 'type-3-light' : 'type-3'}
        paddingTop="0"
        paddingBottom="0"
        visible
        fullWidth
      />
    );
  }

  return null;
}

export default DashboardLayoutTopMessage;
